import { supabase } from './client';
import ErrorHandler from '@/utils/ErrorHandler';

export const getBillingInfo = async () => {
    const { data, error } = await supabase.functions.invoke('get-billing-info', {
        body: {},
    });

    if (error || data?.error) {
        const message = data?.error || error?.message || 'Something went wrong fetching billing info';
        ErrorHandler(Error(error?.message), { mutation: "getBillingInfo", error }, message);
        throw new Error(message);
    }

    return data;
};

// Edge functions return { error } with a non-2xx status; supabase-js puts the
// Response on error.context. Pull the server's message out when available so
// callers can surface real reasons (403 not-admin, 422 conflicts, ...).
const invokeErrorMessage = async (error, data, fallback) => {
    if (data?.error) return data.error;
    try {
        const body = await error?.context?.json?.();
        if (body?.error) return body.error;
    } catch {
        // body unavailable — fall through to the generic message
    }
    return error?.message || fallback;
};

/**
 * Plan transition: target 'paid' opens/resumes verified paid setup
 * ({status:'requires_setup', setupUrl} or {status:'activated'});
 * target 'free' requires confirmation=true and either cancels within the
 * first-period guarantee ('canceled_within_guarantee'), cancels an abandoned
 * setup ('setup_canceled'), or schedules the downgrade at period end
 * ('scheduled'). Server decides which path applies.
 */
export const changeOrganizationPlan = async ({ target, confirmation, operationKey, returnURL } = {}) => {
    const { data, error } = await supabase.functions.invoke('change-organization-plan', {
        body: { target, confirmation, operationKey, returnURL },
    });

    if (error || data?.error) {
        const message = await invokeErrorMessage(error, data, 'Something went wrong changing the plan');
        ErrorHandler(Error(message), { mutation: "changeOrganizationPlan", target, error });
        throw new Error(message);
    }

    return data;
};

/** Stripe billing portal session for the org's existing customer → { portalUrl }. */
export const createBillingPortalSession = async ({ returnURL } = {}) => {
    const { data, error } = await supabase.functions.invoke('create-billing-portal-session', {
        body: { returnURL },
    });

    if (error || data?.error) {
        const message = await invokeErrorMessage(error, data, 'Something went wrong opening the billing portal');
        ErrorHandler(Error(message), { mutation: "createBillingPortalSession", error });
        throw new Error(message);
    }

    return data;
};
